import * as React from 'react';
import { FileWithPath, useDropzone } from 'react-dropzone';

import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { IconDelete } from '@lightspeed/flame/Icon/Delete';
import { Spinner } from '@lightspeed/flame/Spinner';
import { Text } from '@lightspeed/flame/Text';

import { ConfigContext } from 'root/apps/contexts/ConfigContext';
import { ErrorMessageContext } from 'root/apps/InventoryCount/contexts/ErrorMessage';
import { StatusDataContext } from 'root/apps/InventoryCount/contexts/StatusData';

import { StyledBox } from './Dropzone.style';
import ImportErrorFile from './ImportErrorFile';

const ImportFile: React.FC = () => {
  const { getConfig } = React.useContext(ConfigContext);
  const { error, setError } = React.useContext(ErrorMessageContext);
  const [email, setEmail] = React.useState<string | undefined>('');
  const [uploadedFile, setUploadedFiles] = React.useState<Array<File>>([]);
  const { isLoading, data, UseFileUploader, ClearImportSuccessMessage } =
    React.useContext(StatusDataContext);
  const [isHover, setIsHover] = React.useState(false);
  const [isBaseStyleHeight, setIsBaseStyleHeight] = React.useState(false);

  const maxItems = getConfig('maxItems', 8000);

  React.useEffect(() => {
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setUploadedFiles([]);

    if (error) {
      setIsBaseStyleHeight(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  React.useEffect(() => {
    if (data?.notificationEmail) {
      setEmail(data.notificationEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.notificationEmail]);

  React.useEffect(() => {
    const pannel = window.getListingPannel('inventory_count_listings_inventory_count_items_view');
    if (pannel && pannel.listing) {
      pannel.listing.draw_all = true;
    }

    if (data?.status === 'import_success' && pannel) {
      window.RefreshListing(`${pannel.id}`);
    }

    if (data?.status !== 'awaiting_upload') {
      setIsBaseStyleHeight(true);
    } else {
      setIsBaseStyleHeight(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.status]);

  const byteSize = 1000 * 10000;

  const {
    getRootProps,
    getInputProps,
    open,
    acceptedFiles,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    maxSize: byteSize,
    maxFiles: 1,
    multiple: false,
    accept: {
      'text/csv': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
    },
    // Disable click and keydown behavior
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      setUploadedFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );
    },
  });

  const fileName = acceptedFiles.map((file: FileWithPath) => {
    let trimmedFileName;
    const fileExt = file.name.split('.').pop();
    if (file.name.length > 30) {
      trimmedFileName = `${file.name.substring(0, 25)}...${fileExt}`;
    } else {
      trimmedFileName = file.name;
    }
    return (
      <span key={file.path} className="notranslate" style={{ fontSize: '1rem' }}>
        {trimmedFileName}
      </span>
    );
  });

  const baseStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: isBaseStyleHeight ? '225px' : '300px',
    padding: '30px',
    borderWidth: 3,
    borderRadius: 2,
    borderColor: `${theme.colors['gray-100']}`,
    borderStyle: uploadedFile.length > 0 ? 'solid' : 'dashed',
    outline: 'none',
    transition: 'border .24s ease-in-out',
  };

  const acceptStyle = {
    borderColor: `${theme.colors['blue-300']}`,
    borderStyle: 'solid',
  };

  const rejectStyle = {
    borderColor: `${theme.colors['maple-400']}`,
    borderStyle: 'solid',
  };

  const style = () => ({
    ...baseStyle,
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {}),
  });

  const removeUploadedFile = () => {
    acceptedFiles.splice(0, 1);
    setUploadedFiles(
      acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      )
    );
  };

  const submitFile = () => {
    UseFileUploader(uploadedFile[0], email);
  };

  const clearSuccessStatus = () => {
    ClearImportSuccessMessage();
    setIsBaseStyleHeight(false);
  };

  return (
    <Box data-test="upload-view" p={2} height={350}>
      {error ? (
        <Alert data-test="alert-error-import-file" type="danger" noCloseBtn mb={2}>
          {error.message}
        </Alert>
      ) : null}
      {data?.status === 'validation_failed' && !error ? (
        <ImportErrorFile totalErrors={data.totalErrors} />
      ) : null}
      {data?.status === 'import_success' && !error ? (
        <Alert type="success" mb={2} onClose={() => clearSuccessStatus()}>
          Items imported to &quot;Counted items&quot; table
        </Alert>
      ) : null}
      <Box {...getRootProps()} style={style()}>
        <input {...getInputProps()} data-test="drop-input" />
        {uploadedFile.length > 0 ? (
          <Box
            style={{
              border: `1px solid ${theme.colors['gray-100']}`,
              width: '100%',
              height: '80px',
              backgroundColor: `${theme.colors['gray-50']}`,
            }}
          >
            <Flex justifyContent="space-between" alignItems="center" height="100%">
              <Box pl={3} width="170px" flexGrow={1}>
                <Text fontWeight={600} color={theme.colors.black}>
                  {fileName}
                </Text>
                <Text
                  pl="6px"
                  color={theme.colors.textDimmed}
                  fontWeight={theme.fontWeights.regular}
                >
                  {isDragAccept && 'Release file to upload'}
                  {isDragReject && 'File not supported'}
                  {!isDragActive && 'File has been uploaded'}
                </Text>
              </Box>
              <StyledBox
                mr={2}
                onClick={() => removeUploadedFile()}
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
              >
                <IconDelete baseColor={isHover ? theme.colors.white : theme.colors.black} />
              </StyledBox>
            </Flex>
          </Box>
        ) : (
          <>
            <Text fontSize="large" fontWeight={600} color={theme.colors.black}>
              {isDragAccept && 'Release file to upload'}
              {isDragReject && 'File not supported'}
              {!isDragActive && 'Drag and drop file to import'}
            </Text>
            <Text m={0} color={theme.colors.textDimmed} data-test="max-items-text">
              Import up to <var>{maxItems.toLocaleString()}</var> items to the table with a CSV or
              XLSX file.
            </Text>
            <Text m={0} color={theme.colors.textDimmed}>
              Only the first sheet of XLSX files can be imported.
            </Text>
            {!isDragActive ? (
              <Button height={2} mt={1} p={0} size="small" variant="default" fill onClick={open}>
                Select file
              </Button>
            ) : null}
          </>
        )}
      </Box>
      <Flex justifyContent="flex-end">
        <Button
          data-test="map-column-btn"
          height={30}
          width={120}
          mt={1}
          p={0}
          size="small"
          variant="secondary"
          fill
          className="track-inventory-count-import-map-btn"
          disabled={uploadedFile.length === 0}
          onClick={() => submitFile()}
        >
          {isLoading ? <Spinner /> : 'Map columns'}
        </Button>
      </Flex>
    </Box>
  );
};

export default ImportFile;
